import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import { Property } from '@propertypal/shared/src/types/property';
import { getGalleryImages } from '@propertypal/shared/src/utils/property';
import { GalleryItem } from '@propertypal/shared/src/utils/property/getGalleryImages';
import { isHandheldDevice } from '@propertypal/shared/src/utils/support';
import useTouchDevice from '@propertypal/web-app/src/hooks/useTouchDevice';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import PropertyBanner from '../property-box/PropertyBanner';
import { ArrowButton, Container, HtmlWrapper, Image, SLIDE_WIDTH, SliderBox } from './DevelopmentHeader.style';

interface Props {
  html: string;
  property: Property;
  onOpenSlideshowClick: () => void;
  onImageClick: (index: number) => void;
}

const DevelopmentHeader: FunctionComponent<Props> = (props) => {
  const windowSize = useWindowSize();
  const theme = useTheme();
  const touchDevice = useTouchDevice();
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [slidesPerGroup, setSlidesPerGroup] = useState<number | null>(null);
  const images = getGalleryImages(props.property, 'hd', false, false, false, true);

  const checkForStaleVideos = (el: HTMLButtonElement) => {
    // Chrome sometimes fails to autoplay videos inserted via dangerouslySetInnerHTML
    // Only happens when coming from another page
    const videos = el?.querySelectorAll('video');

    if (videos && videos.length && !isHandheldDevice()) {
      videos.forEach((video) => {
        try {
          video.play();
        } catch {
          // silent error
        }
      });
    }
  };

  const handleClick = (image: GalleryItem) => {
    const index = props.property.images?.findIndex((i) => i.displayOrder === image.displayOrder);
    props.onImageClick(index || 0);
  };

  useEffect(() => {
    if (windowSize.width && slidesPerGroup === null) {
      setSlidesPerGroup(Math.floor(windowSize.width / SLIDE_WIDTH));
    }
  }, [windowSize.width]);

  useEffect(() => {
    const els = document.querySelectorAll('.pp-content-top a');

    if (els) {
      els.forEach((el) => {
        el.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      });
    }
  }, []);

  return (
    <Container>
      <PropertyBanner property={props.property} />

      <HtmlWrapper
        aria-label="Open image gallery"
        onClick={props.onOpenSlideshowClick}
        type="button"
        data-testid="openDevelopmentSlideshow"
        dangerouslySetInnerHTML={{ __html: props.html }}
        className="pp-development-content"
        ref={checkForStaleVideos}
      />

      {props.property.type === 'property' && slidesPerGroup !== null && (
        <SliderBox>
          {!touchDevice && (
            <>
              <ArrowButton aria-label="Previous image" side="left" onClick={() => swiper?.slidePrev()}>
                <FontAwesomeIcon icon={faAngleLeft} color={theme.white} style={{ fontSize: 60 }} />
              </ArrowButton>

              <ArrowButton aria-label="Next image" side="right" onClick={() => swiper?.slideNext()}>
                <FontAwesomeIcon icon={faAngleRight} color={theme.white} style={{ fontSize: 60 }} />
              </ArrowButton>
            </>
          )}

          <Swiper
            onSwiper={setSwiper}
            slidesPerView="auto"
            spaceBetween={10}
            slidesPerGroup={slidesPerGroup}
            simulateTouch
          >
            {images?.map((image) => (
              <SwiperSlide key={image.key} style={{ width: 256 }}>
                <Image url={image.image} onClick={() => handleClick(image)} data-testid="development-thumbnail" />
              </SwiperSlide>
            ))}
          </Swiper>
        </SliderBox>
      )}
    </Container>
  );
};

export default DevelopmentHeader;
